/**
 * chez Lavaux's — small moments of delight (Kenan 2026-10-09).
 *
 * • Wishlist: one store for every heart on the site (product cards, product page). It lives in
 *   localStorage as `cl_wish` = [{ id, handle }] (newest first) and is mirrored to the older `chexz_wish`
 *   (ids only) for anything that still reads it. Every change dispatches `cl:wishlist-change` on document,
 *   which keeps the hearts, the header count and the wishlist drawer in step (other tabs via `storage`).
 * • Wishlist drawer: a <theme-drawer> (same side, squeeze and focus handling as the cart drawer) cloned
 *   from snippets/cl-delight.liquid on first use. Rows are rendered by Liquid —
 *   /products/{handle}?view=cl-wish-row (templates/product.cl-wish-row.liquid) — so prices, formats and
 *   Arabic strings stay server-side. Ids saved before handles were stored are kept but not listed until a
 *   heart on the page supplies their handle; products that no longer exist are dropped.
 * • Heart burst: saving springs a small burst of crystal hearts from the button.
 * • Fly to cart: wraps window.chexzCartAdd (assets/chexz-cart-bridge.js) so every add-to-cart in the theme
 *   gets the same moment — the product image arcs into the header bag, the bag bumps, then the cart drawer
 *   opens with the new line briefly highlighted.
 *
 * Motion is transform/opacity only (Web Animations API) on one overlay layer that exists only while
 * something moves. prefers-reduced-motion keeps every state change, the toast and the screen-reader
 * announcements, and drops the motion.
 */

const STORE_KEY = 'cl_wish';
const LEGACY_KEY = 'chexz_wish';
const CHANGE_EVENT = 'cl:wishlist-change';
const DRAWER_ID = 'cl-wish-drawer';
const CART_DRAWER_ID = 'cart-drawer';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const modalDrawerQuery = window.matchMedia('(max-width: 989px)');
const canPopover = typeof HTMLElement.prototype.showPopover === 'function';
const canAnimate = typeof Element.prototype.animate === 'function';

/** @returns {boolean} */
const motionAllowed = () => canAnimate && !reducedMotion.matches;

/** @returns {string} locale-aware storefront root, e.g. "/" or "/ar/" */
const storefrontRoot = () => window.Shopify?.routes?.root ?? '/';

/** @type {Record<string, string>} */
const strings = (() => {
  try {
    return JSON.parse(document.getElementById('cl-delight-strings')?.textContent ?? '{}');
  } catch (error) {
    return {};
  }
})();

/**
 * @param {string} key
 * @param {Record<string, string | number>} [vars]
 * @returns {string}
 */
function t(key, vars = {}) {
  let text = strings[key] ?? '';
  for (const [name, value] of Object.entries(vars)) text = text.replaceAll(`{${name}}`, String(value));
  return text;
}

/* ================================================================
   Wishlist store
   ================================================================ */

/**
 * @typedef {{ id: string, handle: string }} WishEntry
 */

/**
 * @param {string} key
 * @returns {unknown}
 */
function readJSON(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch (error) {
    return null;
  }
}

/** @returns {WishEntry[]} */
function readWishlist() {
  /** @type {WishEntry[]} */
  const list = [];
  const seen = new Set();
  const stored = readJSON(STORE_KEY);

  if (Array.isArray(stored)) {
    for (const entry of stored) {
      const id = String(entry?.id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      list.push({ id, handle: typeof entry.handle === 'string' ? entry.handle : '' });
    }
  }

  // Ids saved by the previous wishlist code have no handle yet; keep them so their hearts stay filled.
  const legacy = readJSON(LEGACY_KEY);
  if (Array.isArray(legacy)) {
    for (const value of legacy) {
      const id = String(value ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      list.push({ id, handle: '' });
    }
  }

  return list;
}

/**
 * @param {WishEntry[]} list
 * @param {{ id?: string, added?: boolean, source?: string }} [detail]
 */
function writeWishlist(list, detail = {}) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(list));
    localStorage.setItem(LEGACY_KEY, JSON.stringify(list.map((entry) => entry.id)));
  } catch (error) {
    // Storage blocked (private mode): the change still applies to this page view.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { ...detail, list } }));
}

/** @param {WishEntry[]} list @returns {WishEntry[]} entries that can be listed */
const listable = (list) => list.filter((entry) => entry.handle);

/** Fills in handles for old id-only entries from hearts rendered on this page. */
function healHandles() {
  const list = readWishlist();
  let changed = false;

  for (const entry of list) {
    if (entry.handle) continue;
    for (const button of document.querySelectorAll('[data-cl-wish][data-cl-wish-handle]')) {
      const element = /** @type {HTMLElement} */ (button);
      if (element.dataset.clWish === entry.id && element.dataset.clWishHandle) {
        entry.handle = element.dataset.clWishHandle;
        changed = true;
        break;
      }
    }
  }

  if (changed) writeWishlist(list, { source: 'heal' });
}

/** @param {WishEntry[]} list */
function syncHearts(list) {
  const ids = new Set(list.map((entry) => entry.id));
  for (const button of document.querySelectorAll('[data-cl-wish]')) {
    const element = /** @type {HTMLElement} */ (button);
    const pressed = String(ids.has(element.dataset.clWish ?? ''));
    if (element.getAttribute('aria-pressed') !== pressed) element.setAttribute('aria-pressed', pressed);
  }
}

/** @param {WishEntry[]} list */
function syncCounts(list) {
  const count = listable(list).length;
  const label = count > 99 ? '99+' : String(count);

  for (const badge of document.querySelectorAll('[data-cl-wish-count]')) {
    const element = /** @type {HTMLElement} */ (badge);
    element.textContent = label;
    element.hidden = count === 0;
  }

  for (const trigger of document.querySelectorAll('[data-cl-wish-open]')) {
    trigger.setAttribute('aria-label', count ? t('wishlistCount', { count }) : t('wishlist'));
  }
}

/**
 * Saves or removes the product behind a heart button.
 * @param {HTMLElement} button - Any element with data-cl-wish (and ideally data-cl-wish-handle / -title)
 */
function toggleFromHeart(button) {
  const id = button.dataset.clWish;
  if (!id) return;

  const list = readWishlist();
  const index = list.findIndex((entry) => entry.id === id);
  const added = index === -1;
  const title = button.dataset.clWishTitle ?? '';

  if (added) {
    list.unshift({ id, handle: button.dataset.clWishHandle ?? '' });
  } else {
    list.splice(index, 1);
  }

  writeWishlist(list, { id, added, source: 'heart' });

  if (added) celebrate(button);
  toast(t(added ? 'savedToast' : 'removedToast'), added);
  if (title) announce(t(added ? 'savedAnnounce' : 'removedAnnounce', { title }));
}

/* ================================================================
   Feedback: announcements and toast
   ================================================================ */

/**
 * Announces a message politely. Content outside an open modal dialog is inert (hidden from assistive
 * technology), so the message goes to a live region inside the topmost modal when there is one.
 * @param {string} message
 */
function announce(message) {
  if (!message) return;

  const modals = document.querySelectorAll('dialog:modal');
  const host = modals.length ? /** @type {HTMLElement} */ (modals[modals.length - 1]) : document.body;
  let region = /** @type {HTMLElement | null} */ (host.querySelector(':scope > [data-cl-live]'));

  if (!region) {
    region = document.createElement('p');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.dataset.clLive = '';
    host.append(region);
  }

  const target = region;
  target.textContent = '';
  window.setTimeout(() => {
    target.textContent = message;
  }, 80);
}

const HEART_PATH =
  'M12 21s-7.6-4.6-9.5-9.4C1.2 8 3.4 4.5 6.9 4.5c2.1 0 3.6 1.1 5.1 3 1.5-1.9 3-3 5.1-3 3.5 0 5.7 3.5 4.4 7.1C19.6 16.4 12 21 12 21z';

/** @type {{ element: HTMLElement | null, timer: number }} */
const toastState = { element: null, timer: 0 };

/**
 * Shows a short, quiet confirmation at the bottom of the screen (visual only — see announce()).
 * @param {string} message
 * @param {boolean} filled - Filled heart (saved) or outline heart (removed)
 */
function toast(message, filled) {
  if (!message) return;

  let element = toastState.element;
  if (!element) {
    element = document.createElement('div');
    element.className = 'cl-toast';
    element.setAttribute('aria-hidden', 'true');
    if (canPopover) element.popover = 'manual';
    element.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${HEART_PATH}" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg><span></span>`;
    document.body.append(element);
    toastState.element = element;
  }

  const toastElement = element;
  toastElement.querySelector('span').textContent = message;
  toastElement.querySelector('path')?.setAttribute('fill', filled ? 'currentColor' : 'none');

  // Re-show so it stacks above a drawer that opened after it (top layer order follows show order).
  if (canPopover) {
    if (toastElement.matches(':popover-open')) toastElement.hidePopover();
    toastElement.showPopover();
  }

  toastElement.classList.remove('is-visible');
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => toastElement.classList.add('is-visible'));
  });

  window.clearTimeout(toastState.timer);
  toastState.timer = window.setTimeout(() => {
    toastElement.classList.remove('is-visible');
    toastState.timer = window.setTimeout(() => {
      if (canPopover && toastElement.matches(':popover-open')) toastElement.hidePopover();
    }, 320);
  }, 2200);
}

/* ================================================================
   Motion layer
   ================================================================ */

const fxLayer = {
  /** @type {HTMLElement | null} */
  element: null,
  users: 0,

  /** @returns {HTMLElement} */
  acquire() {
    if (!this.element) {
      const element = document.createElement('div');
      element.className = 'cl-fx';
      element.setAttribute('aria-hidden', 'true');
      if (canPopover) element.popover = 'manual';
      document.body.append(element);
      // A manual popover sits in the top layer, so the motion stays visible above open drawers.
      if (canPopover) element.showPopover();
      this.element = element;
    }
    this.users += 1;
    return this.element;
  },

  release() {
    this.users = Math.max(0, this.users - 1);
    if (this.users === 0 && this.element) {
      this.element.remove();
      this.element = null;
    }
  },
};

/** @param {number} min @param {number} max */
const random = (min, max) => min + Math.random() * (max - min);

/**
 * @param {Element | null | undefined} element
 * @param {number} [strength]
 */
function bump(element, strength = 1.2) {
  if (!element || !motionAllowed()) return;
  element.animate(
    [
      { transform: 'scale(1)' },
      { transform: `scale(${strength})`, offset: 0.35 },
      { transform: 'scale(0.94)', offset: 0.7 },
      { transform: 'scale(1)' },
    ],
    { duration: 460, easing: 'cubic-bezier(0.3, 0.7, 0.4, 1)' }
  );
}

/* ---------- Heart burst ---------- */

const HEART_COLOURS = ['#5c1b2e', '#9b3550', '#c4566f', '#e39aac', '#c9a063', '#7a2540'];

/**
 * @param {number} size
 * @param {string} colour
 * @returns {SVGSVGElement}
 */
function crystalHeart(size, colour) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.classList.add('cl-fx__heart');
  svg.style.color = colour;
  // A filled heart with two pale facets, echoing the rose-crystal illustrations of the store.
  svg.innerHTML = `<path d="${HEART_PATH}" fill="currentColor"/><path d="M12 7.5 6.9 4.5 2.9 8.4 12 21z" fill="#fff" fill-opacity=".26"/><path d="M12 7.5l5.1-3 4 3.9z" fill="#fff" fill-opacity=".16"/>`;
  return svg;
}

/** @param {number} value @param {number} min @param {number} max */
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** @param {number} ms @returns {Promise<void>} */
const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * A soft blur over the whole page while a celebration plays (one backdrop-filter element).
 * Kenan 2026-10-10: «قلوب تطلع على ملء الشاشة سريعة مع بلور بالخلفية».
 * @param {HTMLElement} layer
 * @returns {{ fadeOut: (duration: number) => Promise<void> }}
 */
function veil(layer) {
  const element = document.createElement('div');
  element.className = 'cl-fx__veil';
  layer.prepend(element);
  element.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out', fill: 'forwards' });

  return {
    fadeOut(duration) {
      return element
        .animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing: 'ease-in', fill: 'forwards' })
        .finished.catch(() => {})
        .then(() => element.remove());
    },
  };
}

/**
 * Saving a perfume: the page blurs, a large crystal heart pops in the centre and a field of hearts
 * rises fast across the whole screen (a few start from the pressed button), all within ~1.2s.
 * @param {HTMLElement} button
 */
function celebrate(button) {
  if (!motionAllowed()) return;

  bump(button.querySelector('svg'), 1.35);
  window.setTimeout(() => {
    for (const icon of document.querySelectorAll('[data-cl-wish-open] [data-cl-bump]')) {
      if (icon.getBoundingClientRect().width) bump(icon, 1.18);
    }
  }, 380);

  const layer = fxLayer.acquire();
  const width = window.innerWidth;
  const height = window.innerHeight;
  const rect = button.getBoundingClientRect();
  const blur = veil(layer);
  /** @type {Promise<unknown>[]} */
  const finished = [];

  finished.push(wait(760).then(() => blur.fadeOut(420)));

  const bigSize = Math.round(clamp(Math.min(width, height) * 0.24, 84, 132));
  const big = crystalHeart(bigSize, '#7a2540');
  big.classList.add('cl-fx__heart--big');
  big.style.left = `${width / 2 - bigSize / 2}px`;
  big.style.top = `${height * 0.46 - bigSize / 2}px`;
  layer.append(big);
  finished.push(
    big
      .animate(
        [
          { transform: 'scale(0.2)', opacity: 0 },
          { transform: 'scale(1.14)', opacity: 1, offset: 0.26 },
          { transform: 'scale(1)', opacity: 1, offset: 0.5 },
          { transform: 'scale(1.32)', opacity: 0 },
        ],
        { duration: 980, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)', fill: 'both' }
      )
      .finished.catch(() => {})
  );

  const count = Math.round(clamp(width / 13, 26, 52));
  const fromButton = rect.width ? 6 : 0;
  // Spread the starting columns evenly across the screen, then shuffle their start times.
  const columns = Array.from({ length: count }, (_, index) => (index + random(0.15, 0.85)) / count).sort(
    () => Math.random() - 0.5
  );

  for (let index = 0; index < count; index += 1) {
    const size = Math.round(13 + Math.pow(Math.random(), 1.6) * 30);
    const heart = crystalHeart(size, HEART_COLOURS[index % HEART_COLOURS.length]);
    const isFromButton = index < fromButton;
    const startX = isFromButton ? rect.left + rect.width / 2 + random(-10, 10) : columns[index] * width;
    const startY = isFromButton ? rect.top + rect.height / 2 : height + size;
    const rise = startY + size * 1.6 + random(0, height * 0.08);
    const sway = random(16, 44) * (Math.random() < 0.5 ? -1 : 1);
    const spin = random(-28, 28);

    heart.style.left = `${startX - size / 2}px`;
    heart.style.top = `${startY - size / 2}px`;
    layer.append(heart);

    const animation = heart.animate(
      [
        { transform: 'translate(0, 0) rotate(0deg) scale(0.6)', opacity: 0 },
        { transform: `translate(${sway * 0.6}px, ${-rise * 0.22}px) rotate(${spin * 0.3}deg) scale(1)`, opacity: 1, offset: 0.16 },
        { transform: `translate(${-sway * 0.4}px, ${-rise * 0.62}px) rotate(${spin * 0.7}deg) scale(1)`, opacity: 0.95, offset: 0.62 },
        { transform: `translate(${sway * 0.3}px, ${-rise}px) rotate(${spin}deg) scale(0.9)`, opacity: 0 },
      ],
      {
        duration: random(760, 1060),
        delay: isFromButton ? random(0, 60) : random(30, 300),
        easing: 'cubic-bezier(0.3, 0.55, 0.4, 1)',
        fill: 'both',
      }
    );
    finished.push(animation.finished.catch(() => {}));
  }

  Promise.all(finished).then(() => {
    for (const heart of layer.querySelectorAll('.cl-fx__heart')) heart.remove();
    fxLayer.release();
  });
}

/* ---------- Fly to cart ---------- */

/** @type {{ element: Element | null, time: number }} */
const lastActivation = { element: null, time: 0 };

document.addEventListener(
  'click',
  (event) => {
    lastActivation.element = event.target instanceof Element ? event.target : null;
    lastActivation.time = performance.now();
  },
  true
);

/**
 * The largest image inside a container that is actually on screen (gallery tracks keep their other
 * slides scrolled out of view).
 * @param {Element} container
 * @returns {HTMLImageElement | null}
 */
function visibleImage(container) {
  const bounds = container.getBoundingClientRect();
  /** @type {HTMLImageElement | null} */
  let best = null;
  let bestArea = 0;

  for (const image of container.querySelectorAll('img')) {
    const rect = image.getBoundingClientRect();
    const width = Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left);
    const height = Math.min(rect.bottom, bounds.bottom) - Math.max(rect.top, bounds.top);
    const area = Math.max(0, width) * Math.max(0, height);
    if (area > bestArea) {
      best = /** @type {HTMLImageElement} */ (image);
      bestArea = area;
    }
  }

  return best;
}

/**
 * Where the flying image starts: the product tile (cards, wishlist rows, sticky bar thumbnail, product
 * gallery) of the button that was pressed.
 * @param {Element} trigger
 * @returns {{ x: number, y: number, width: number, height: number, src: string } | null}
 */
function flySource(trigger) {
  const scopes = [
    ['.cl-pcard', '.cl-pcard__media'],
    ['.cl-wish-row', '.cl-wish-row__media'],
    ['.shpdp-sticky', null],
    ['.shpdp', '.shpdp-gallery__stage'],
    ['[data-product-id]', null],
    ['product-card', null],
  ];

  for (const [scopeSelector, mediaSelector] of scopes) {
    const scope = trigger.closest(/** @type {string} */ (scopeSelector));
    if (!scope) continue;

    const media = (mediaSelector && scope.querySelector(mediaSelector)) || scope;
    const image = visibleImage(media);
    if (!image) return null;

    const src = image.currentSrc || image.src;
    if (!src) return null;

    let rect = (mediaSelector ? media : image).getBoundingClientRect();
    const onScreen = rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    // The tile is scrolled away (e.g. sticky bar on the product page): start from the button instead.
    if (!onScreen) rect = trigger.getBoundingClientRect();
    if (!rect.width) return null;

    // Large galleries lift off as a card-sized tile rather than a half-screen sheet.
    const scale = Math.min(1, 280 / Math.max(rect.width, rect.height));
    const width = Math.max(44, rect.width * scale);
    const height = Math.max(44, rect.height * scale);

    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, width, height, src };
  }

  return null;
}

/**
 * The mobile tab bar's cart tab, when the bar is on screen (phones) and not making room for the product
 * page's sticky add-to-cart bar. The bar is revealed first; its resting position is the target.
 * @returns {{ x: number, y: number, size: number, element: Element | null } | null}
 */
function tabbarCartTarget() {
  const bar = /** @type {any} */ (document.querySelector('cl-tabbar'));
  const icon = bar?.querySelector('[data-cl-cart-icon]');
  if (!bar || !icon || getComputedStyle(bar).display === 'none') return null;
  if (document.querySelector('.shpdp-sticky.is-visible')) return null;

  const offset = new DOMMatrixReadOnly(getComputedStyle(bar).transform).m42;
  const rect = icon.getBoundingClientRect();
  if (!rect.width) return null;
  bar.reveal?.();

  return {
    x: rect.left + rect.width / 2,
    y: rect.top - offset + rect.height / 2,
    size: 22,
    element: icon,
  };
}

/** @returns {{ x: number, y: number, size: number, element: Element | null } | null} */
function cartTarget() {
  const tab = tabbarCartTarget();
  if (tab) return tab;

  const icon =
    document.querySelector('header-actions cart-icon .svg-wrapper') ?? document.querySelector('cart-icon .svg-wrapper');
  const rtl = document.documentElement.dir === 'rtl';

  if (!icon) return null;

  const rect = icon.getBoundingClientRect();
  if (!rect.width) {
    return { x: rtl ? 36 : window.innerWidth - 36, y: 30, size: 22, element: null };
  }

  // A header that has slid up out of view is still the destination: aim at the top edge above it.
  return {
    x: rect.left + rect.width / 2,
    y: Math.max(rect.top + rect.height / 2, 18),
    size: Math.min(rect.width, rect.height) * 0.5,
    element: icon,
  };
}

const CHECK_PATH = 'M6 12.5l4 4 8-9';
const SPARK_PATH = 'M12 2l2 7.9 8 2.1-8 2.1-2 7.9-2-7.9-8-2.1 8-2.1z';

/**
 * The "added to bag" moment (Kenan 2026-10-10: «وقت ضيف منتج للسلة بدي حركة تظهر للعميل حلوة»):
 * the page blurs, the product tile lifts to the centre as a white card, and once the cart has really
 * accepted it a wine check badge pops on its corner with sparkles and a caption. Then the card glides
 * into the bag (header, or the tab bar on phones). If the request fails the card just fades away, so
 * the caption never claims an add that didn't happen.
 * Two transforms make each move: the outer box moves along x, the inner along y.
 * @param {{ x: number, y: number, width: number, height: number, src: string }} source
 * @param {{ x: number, y: number, size: number, element: Element | null }} target
 * @param {Promise<unknown>} request - the add-to-cart call
 * @returns {Promise<void>} resolves when the card has landed (or faded after a failed add)
 */
async function presentAdded(source, target, request) {
  const layer = fxLayer.acquire();
  const blur = veil(layer);
  const width = window.innerWidth;
  const height = window.innerHeight;
  const rtl = document.documentElement.dir === 'rtl';
  const cardSize = Math.round(clamp(Math.min(width, height) * 0.42, 150, 220));
  const centreX = width / 2 - source.x;
  const centreY = height * 0.44 - source.y;

  const outer = document.createElement('div');
  const inner = document.createElement('div');
  const card = document.createElement('div');
  const image = document.createElement('img');

  outer.className = 'cl-fx__fly';
  inner.className = 'cl-fx__fly-y';
  card.className = 'cl-fx__fly-card';
  outer.style.left = `${source.x}px`;
  outer.style.top = `${source.y}px`;
  card.style.width = `${cardSize}px`;
  card.style.height = `${cardSize}px`;
  card.style.left = `${-cardSize / 2}px`;
  card.style.top = `${-cardSize / 2}px`;
  image.src = source.src;
  image.alt = '';
  image.decoding = 'async';

  card.append(image);
  inner.append(card);
  outer.append(inner);
  layer.append(outer);

  const startScale = Math.max(source.width, source.height) / cardSize;
  const lift = { duration: 420, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)', fill: /** @type {const} */ ('forwards') };
  outer.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${centreX}px)` }], lift);
  inner.animate([{ transform: 'translateY(0)' }, { transform: `translateY(${centreY}px)` }], lift);
  await card
    .animate(
      [
        { transform: `scale(${startScale})`, opacity: 0.9 },
        { transform: 'scale(1.05)', opacity: 1, offset: 0.7 },
        { transform: 'scale(1)', opacity: 1 },
      ],
      lift
    )
    .finished.catch(() => {});

  const added = await Promise.race([
    request.then(
      () => true,
      () => false
    ),
    wait(6000).then(() => false),
  ]);

  if (!added) {
    await Promise.all([
      card.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.9)' }], { duration: 240, fill: 'forwards' }).finished.catch(() => {}),
      blur.fadeOut(260),
    ]);
    outer.remove();
    fxLayer.release();
    return;
  }

  // Check badge on the card's top inline-end corner, a caption under it, and a few sparkles.
  const corner = cardSize / 2 - 8;
  const badge = document.createElement('span');
  badge.className = 'cl-fx__badge';
  badge.style.left = `${rtl ? -corner : corner}px`;
  badge.style.top = `${-corner}px`;
  badge.innerHTML = `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="${CHECK_PATH}" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" pathLength="1" stroke-dasharray="1" stroke-dashoffset="1"/></svg>`;
  const caption = document.createElement('span');
  caption.className = 'cl-fx__caption';
  caption.textContent = t('addedCaption');
  caption.style.top = `${cardSize / 2 + 18}px`;
  inner.append(badge, caption);

  badge.animate(
    [
      { transform: 'scale(0)', opacity: 0 },
      { transform: 'scale(1.18)', opacity: 1, offset: 0.6 },
      { transform: 'scale(1)', opacity: 1 },
    ],
    { duration: 320, easing: 'cubic-bezier(0.3, 0.7, 0.4, 1)', fill: 'forwards' }
  );
  badge.querySelector('path')?.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], {
    duration: 280,
    delay: 140,
    easing: 'ease-out',
    fill: 'forwards',
  });
  if (caption.textContent) {
    caption.animate(
      [
        { transform: 'translateY(8px)', opacity: 0 },
        { transform: 'translateY(0)', opacity: 1 },
      ],
      { duration: 260, delay: 80, easing: 'ease-out', fill: 'forwards' }
    );
  }

  for (let index = 0; index < 9; index += 1) {
    const size = Math.round(random(9, 17));
    const spark = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    spark.setAttribute('viewBox', '0 0 24 24');
    spark.setAttribute('width', String(size));
    spark.setAttribute('height', String(size));
    spark.classList.add('cl-fx__spark');
    spark.innerHTML = `<path d="${SPARK_PATH}" fill="currentColor"/>`;
    spark.style.color = index % 3 === 0 ? '#5c1b2e' : '#c9a063';
    const angle = (index / 9) * Math.PI * 2 + random(-0.25, 0.25);
    const from = cardSize * 0.5;
    const to = from + random(22, 44);
    spark.style.left = `${-size / 2}px`;
    spark.style.top = `${-size / 2}px`;
    inner.append(spark);
    spark.animate(
      [
        { transform: `translate(${Math.cos(angle) * from}px, ${Math.sin(angle) * from}px) scale(0) rotate(0deg)`, opacity: 0 },
        { transform: `translate(${Math.cos(angle) * (from + 10)}px, ${Math.sin(angle) * (from + 10)}px) scale(1) rotate(45deg)`, opacity: 1, offset: 0.35 },
        { transform: `translate(${Math.cos(angle) * to}px, ${Math.sin(angle) * to}px) scale(0.4) rotate(90deg)`, opacity: 0 },
      ],
      { duration: 620, delay: random(40, 160), easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'both' }
    );
  }

  await wait(620);

  // Into the bag: the badge and caption leave first, the card shrinks on an arc (a small pull back,
  // then it speeds into the icon) while the blur lifts.
  const leave = { duration: 160, easing: 'ease-in', fill: /** @type {const} */ ('forwards') };
  badge.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.6)' }], leave);
  caption.animate([{ opacity: 1 }, { opacity: 0 }], leave);
  blur.fadeOut(520);

  const duration = 560;
  const endScale = Math.max(target.size / cardSize, 0.05);
  outer.animate([{ transform: `translateX(${centreX}px)` }, { transform: `translateX(${target.x - source.x}px)` }], {
    duration,
    easing: 'cubic-bezier(0.45, 0, 0.6, 1)',
    fill: 'forwards',
  });
  inner.animate([{ transform: `translateY(${centreY}px)` }, { transform: `translateY(${target.y - source.y}px)` }], {
    duration,
    easing: 'cubic-bezier(0.5, -0.3, 0.75, 0.5)',
    fill: 'forwards',
  });
  await card
    .animate(
      [
        { transform: 'scale(1)', opacity: 1 },
        { transform: 'scale(1.04)', opacity: 1, offset: 0.12 },
        { transform: `scale(${Math.max(endScale * 2.4, 0.16)})`, opacity: 0.95, offset: 0.8 },
        { transform: `scale(${endScale})`, opacity: 0.2 },
      ],
      { duration, easing: 'cubic-bezier(0.45, 0, 0.55, 1)', fill: 'forwards' }
    )
    .finished.catch(() => {});

  outer.remove();
  bump(target.element, 1.24);
  ring(layer, target);
}

/**
 * A faint wine ring where the tile lands.
 * @param {HTMLElement} layer
 * @param {{ x: number, y: number }} target
 */
function ring(layer, target) {
  const element = document.createElement('span');
  element.className = 'cl-fx__ring';
  element.style.left = `${target.x}px`;
  element.style.top = `${target.y}px`;
  layer.append(element);

  element
    .animate(
      [
        { transform: 'scale(0.55)', opacity: 0.55 },
        { transform: 'scale(1.75)', opacity: 0 },
      ],
      { duration: 520, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' }
    )
    .finished.catch(() => {})
    .then(() => {
      element.remove();
      fxLayer.release();
    });
}

/**
 * Gives the cart line for this variant a short cream glow once it is in the drawer. The glow runs on
 * the row's ::after through the Web Animations API, so the drawer's morph re-render cannot strip it.
 * @param {string | number} variantId
 */
function highlightLine(variantId) {
  const id = String(variantId);
  if (!motionAllowed() || !/^\d+$/.test(id)) return;

  const started = performance.now();
  const seek = () => {
    const row = document.querySelector(`.cart-drawer__items .cart-items__table-row[data-key^="${id}:"]`);
    if (row) {
      row.animate(
        [
          { opacity: 0 },
          { opacity: 1, offset: 0.12 },
          { opacity: 1, offset: 0.4 },
          { opacity: 0 },
        ],
        { duration: 1700, easing: 'ease-out', pseudoElement: '::after' }
      );
      return;
    }
    if (performance.now() - started < 1600) window.requestAnimationFrame(seek);
  };

  // Let the drawer's section re-render (and its view transition) land first.
  window.setTimeout(() => window.requestAnimationFrame(seek), 120);
}

/**
 * @param {any} cart - ajax cart returned by the bridge
 * @param {string | number} variantId
 * @returns {string}
 */
function lineTitle(cart, variantId) {
  const item = cart?.items?.find?.((/** @type {any} */ line) => String(line.variant_id) === String(variantId));
  return item?.product_title ?? item?.title ?? '';
}

/** Opens the cart drawer the way its own auto-open would. */
function openCartDrawer() {
  const drawer = /** @type {any} */ (document.getElementById(CART_DRAWER_ID));
  if (typeof drawer?.open === 'function') drawer.open();
}

/**
 * Wraps the cart bridge so every add-to-cart in the theme gets the flight. Callers may pass
 * `options.flyFrom` (the button); otherwise the element clicked in the last 1.5s is used.
 */
function wrapCartAdd() {
  const original = window.chexzCartAdd;
  if (typeof original !== 'function' || original.clDelight) return;

  /**
   * @param {{ id: string | number, quantity?: number }} line
   * @param {{ source?: string, flyFrom?: Element }} [options]
   */
  const addWithDelight = async (line, options = {}) => {
    const trigger =
      options.flyFrom instanceof Element
        ? options.flyFrom
        : performance.now() - lastActivation.time < 1500
          ? lastActivation.element
          : null;

    // Over an open modal (phone drawers) the header bag is covered: no flight, the cart simply opens.
    const canFly = motionAllowed() && trigger && !document.querySelector('dialog:modal');
    const source = canFly && trigger ? flySource(trigger) : null;
    const target = source ? cartTarget() : null;

    if (!source || !target) {
      const cart = await original(line, options);
      announce(t('addedAnnounce', { title: lineTitle(cart, line.id) }).trim());
      highlightLine(line.id);
      return cart;
    }

    // Hold the drawer's auto-open until the tile has landed. The bridge dispatches the cart event
    // synchronously, so the attribute is back before anything else can read it.
    const drawerComponent = document.querySelector('cart-drawer-component');
    const autoOpen = Boolean(drawerComponent?.hasAttribute('auto-open'));
    if (autoOpen) drawerComponent?.removeAttribute('auto-open');

    /** @type {Promise<any>} */
    let request;
    try {
      request = Promise.resolve(original(line, options));
    } finally {
      if (autoOpen) drawerComponent?.setAttribute('auto-open', '');
    }

    const landed = presentAdded(source, target, request);
    const [outcome] = await Promise.allSettled([request, landed]);
    if (outcome.status === 'rejected') throw outcome.reason;

    const cart = outcome.value;
    if (autoOpen) openCartDrawer();
    announce(t('addedAnnounce', { title: lineTitle(cart, line.id) }).trim());
    highlightLine(line.id);
    return cart;
  };

  addWithDelight.clDelight = true;
  window.chexzCartAdd = addWithDelight;
}

/* ================================================================
   Wishlist drawer
   ================================================================ */

/** @type {any} */
let drawer = null;
/** @type {Map<string, string>} handle → row HTML, for this page view */
const rowCache = new Map();
let renderToken = 0;

/** @returns {any} the <theme-drawer>, created from its template on first use */
function mountDrawer() {
  if (drawer) return drawer;

  const template = document.getElementById('cl-wish-drawer-template');
  if (!(template instanceof HTMLTemplateElement)) return null;

  document.body.append(template.content.cloneNode(true));
  drawer = document.getElementById(DRAWER_ID);
  syncCounts(readWishlist());

  for (const trigger of document.querySelectorAll('[data-cl-wish-open]')) {
    trigger.setAttribute('aria-controls', DRAWER_ID);
  }

  return drawer;
}

/** @param {HTMLElement | null} element */
const isTopmostOpen = (element) => {
  if (!element?.hasAttribute('open')) return false;
  const order = Number(element.style.getPropertyValue('--drawer-stack-order') || 0);
  for (const other of document.querySelectorAll('theme-drawer[open]')) {
    if (other === element) continue;
    if (Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0) > order) {
      return false;
    }
  }
  return true;
};

/**
 * On wide screens the drawer is a non-modal sidebar whose focus trap is built from the buttons present
 * when it opened. Rows arrive later, so rebuild the trap (and keep focus where it was).
 */
async function retrapFocus() {
  if (!drawer || modalDrawerQuery.matches || !isTopmostOpen(drawer)) return;

  const panel = drawer.querySelector('dialog');
  if (!panel) return;

  try {
    const { trapFocus } = await import('@theme/focus');
    const active = document.activeElement;
    trapFocus(panel);
    if (active instanceof HTMLElement && panel.contains(active)) active.focus();
  } catch (error) {
    // Focus helpers unavailable: the native dialog behaviour still applies.
  }
}

/**
 * @param {string} html
 * @returns {HTMLElement | null}
 */
function rowFromHTML(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content.querySelector('[data-cl-wish-row]');
}

/** @param {WishEntry} entry @returns {HTMLLIElement} */
function skeletonRow(entry) {
  const row = document.createElement('li');
  row.className = 'cl-wish-row cl-wish-row--skeleton';
  row.dataset.clWishSkeleton = entry.id;
  row.setAttribute('aria-hidden', 'true');
  row.innerHTML =
    '<span class="cl-wish-row__media"></span><span class="cl-wish-row__info"><span class="cl-wish-skel cl-wish-skel--brand"></span><span class="cl-wish-skel cl-wish-skel--title"></span><span class="cl-wish-skel cl-wish-skel--price"></span></span>';
  return row;
}

/**
 * @param {WishEntry} entry
 * @returns {Promise<{ html?: string, gone?: boolean }>}
 */
async function loadRow(entry) {
  try {
    const url = `${storefrontRoot()}products/${encodeURIComponent(entry.handle)}?view=cl-wish-row`;
    const response = await fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
    if (response.status === 404) return { gone: true };
    if (!response.ok) return {};

    const html = (await response.text()).trim();
    const row = rowFromHTML(html);
    if (!row) return {};
    // The handle now belongs to another product: the saved one is gone.
    if (row.dataset.clWishRow !== entry.id) return { gone: true };

    rowCache.set(entry.handle, html);
    return { html };
  } catch (error) {
    return {};
  }
}

/** @param {boolean} empty */
function showEmpty(empty) {
  if (!drawer) return;
  const list = drawer.querySelector('[data-cl-wish-list]');
  const emptyState = drawer.querySelector('[data-cl-wish-empty]');
  if (list) list.hidden = empty;
  if (emptyState) emptyState.hidden = !empty;
}

/** Fills the drawer from storage: cached rows at once, the rest as skeletons until Liquid answers. */
async function renderDrawer() {
  if (!drawer) return;

  const list = /** @type {HTMLElement | null} */ (drawer.querySelector('[data-cl-wish-list]'));
  const status = /** @type {HTMLElement | null} */ (drawer.querySelector('[data-cl-wish-status]'));
  if (!list || !status) return;

  const token = (renderToken += 1);
  const entries = listable(readWishlist());
  status.hidden = true;

  if (!entries.length) {
    list.replaceChildren();
    showEmpty(true);
    retrapFocus();
    return;
  }

  showEmpty(false);
  list.replaceChildren(
    ...entries.map((entry) => {
      const cached = rowCache.get(entry.handle);
      return (cached && rowFromHTML(cached)) || skeletonRow(entry);
    })
  );

  const missing = entries.filter((entry) => !rowCache.has(entry.handle));
  if (!missing.length) {
    retrapFocus();
    return;
  }

  list.setAttribute('aria-busy', 'true');
  const results = await Promise.all(missing.map(loadRow));
  if (token !== renderToken) return;
  list.removeAttribute('aria-busy');

  /** @type {string[]} */
  const gone = [];
  let failed = 0;

  missing.forEach((entry, index) => {
    const placeholder = list.querySelector(`[data-cl-wish-skeleton="${entry.id}"]`);
    const result = results[index];
    if (result?.html) {
      const row = rowFromHTML(result.html);
      if (row && placeholder) placeholder.replaceWith(row);
      return;
    }
    placeholder?.remove();
    if (result?.gone) gone.push(entry.id);
    else failed += 1;
  });

  if (gone.length) {
    writeWishlist(
      readWishlist().filter((entry) => !gone.includes(entry.id)),
      { source: 'drawer' }
    );
  }

  status.hidden = failed === 0;
  if (!list.querySelector('[data-cl-wish-row]') && !failed) showEmpty(true);
  retrapFocus();
}

/** Opens (or closes, when it is already the topmost drawer) the wishlist drawer. */
async function toggleDrawer() {
  const element = mountDrawer();
  if (!element) return;

  await customElements.whenDefined('theme-drawer');

  if (isTopmostOpen(element)) {
    element.close();
    return;
  }

  renderDrawer();
  element.open();
}

/** @param {HTMLElement} button */
function removeFromDrawer(button) {
  const row = /** @type {HTMLElement | null} */ (button.closest('[data-cl-wish-row]'));
  const list = drawer?.querySelector('[data-cl-wish-list]');
  const id = row?.dataset.clWishRow;
  if (!row || !list || !id) return;

  const rows = [...list.querySelectorAll('[data-cl-wish-row]')];
  const index = rows.indexOf(row);
  const neighbour = rows[index + 1] ?? rows[index - 1] ?? null;

  writeWishlist(
    readWishlist().filter((entry) => entry.id !== id),
    { id, added: false, source: 'drawer' }
  );
  announce(t('removedAnnounce', { title: row.dataset.title ?? '' }));

  const finish = () => {
    row.remove();
    if (!list.querySelector('[data-cl-wish-row]')) {
      showEmpty(true);
      /** @type {HTMLElement | null} */ (drawer?.querySelector('.cl-empty-state__cta'))?.focus();
    } else {
      /** @type {HTMLElement | null} */ (neighbour?.querySelector('[data-cl-wish-remove]'))?.focus();
    }
    retrapFocus();
  };

  if (!motionAllowed()) {
    finish();
    return;
  }

  const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
  row
    .animate(
      [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: `translateX(${18 * direction}px)` },
      ],
      { duration: 220, easing: 'ease-in', fill: 'forwards' }
    )
    .finished.catch(() => {})
    .then(finish);
}

/** @param {HTMLButtonElement} button */
async function addFromDrawer(button) {
  const variantId = button.dataset.clWishAdd;
  if (!variantId || typeof window.chexzCartAdd !== 'function') return;

  button.disabled = true;
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');
  try {
    await window.chexzCartAdd({ id: variantId, quantity: 1 }, { source: 'cl-wishlist', flyFrom: button });
  } catch (error) {
    // The cart bridge reports the error to the theme.
  } finally {
    button.disabled = false;
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
  }
}

/* ================================================================
   Wiring
   ================================================================ */

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const heart = /** @type {HTMLElement | null} */ (event.target.closest('[data-cl-wish]'));
  if (heart) {
    event.preventDefault();
    toggleFromHeart(heart);
    return;
  }

  if (event.target.closest('[data-cl-wish-open]')) {
    event.preventDefault();
    toggleDrawer();
    return;
  }

  const remove = /** @type {HTMLElement | null} */ (event.target.closest('[data-cl-wish-remove]'));
  if (remove) {
    removeFromDrawer(remove);
    return;
  }

  const add = /** @type {HTMLButtonElement | null} */ (event.target.closest('[data-cl-wish-add]'));
  if (add && !add.disabled) addFromDrawer(add);
});

document.addEventListener(CHANGE_EVENT, (event) => {
  const { list, source } = /** @type {CustomEvent} */ (event).detail ?? {};
  const current = Array.isArray(list) ? list : readWishlist();
  syncHearts(current);
  syncCounts(current);
  if (drawer?.hasAttribute('open') && source !== 'drawer') renderDrawer();
});

// Other tabs.
window.addEventListener('storage', (event) => {
  if (event.key !== STORE_KEY && event.key !== LEGACY_KEY) return;
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list: readWishlist(), source: 'storage' } }));
});

// Back/forward cache restores keep the old DOM state.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  const list = readWishlist();
  syncHearts(list);
  syncCounts(list);
});

// Header trigger state, and the focus trap when a drawer stacked above the wishlist closes.
document.addEventListener('theme-drawer:open', (event) => {
  if (/** @type {HTMLElement} */ (event.target)?.id !== DRAWER_ID) return;
  for (const trigger of document.querySelectorAll('[data-cl-wish-open]')) trigger.setAttribute('aria-expanded', 'true');
});

document.addEventListener('theme-drawer:close', (event) => {
  const closing = /** @type {HTMLElement} */ (event.target);
  if (closing?.id === DRAWER_ID) {
    for (const trigger of document.querySelectorAll('[data-cl-wish-open]')) trigger.setAttribute('aria-expanded', 'false');
    return;
  }
  if (drawer?.hasAttribute('open')) window.setTimeout(retrapFocus, 0);
});

// Cards added later (filters, "load more", carousels rendered on demand) get their hearts filled too.
let syncQueued = false;
new MutationObserver((records) => {
  if (syncQueued) return;
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const element = /** @type {Element} */ (node);
      if (element.matches('[data-cl-wish]') || element.querySelector('[data-cl-wish]')) {
        syncQueued = true;
        window.requestAnimationFrame(() => {
          syncQueued = false;
          healHandles();
          syncHearts(readWishlist());
        });
        return;
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

// First paint of hearts and the header count.
healHandles();
{
  const list = readWishlist();
  syncHearts(list);
  syncCounts(list);
}

// The bridge module normally runs first (it is in <head>); wait for it otherwise.
if (window.chexzCartBridgeReady) {
  wrapCartAdd();
} else {
  document.addEventListener('chexz:cart-bridge-ready', wrapCartAdd, { once: true });
}

window.clWishlist = {
  read: readWishlist,
  open: toggleDrawer,
};
